import { useCrud } from './useCrud';
import { STORAGE_KEYS } from '../models/constants';
import { 
  recordArrangementPayment, 
  isArrangementOverdue, 
  getDaysUntilDue,
  calculateArrears 
} from '../models/PaymentArrangement';
import { 
  calculateMissedPaymentArrears,
  calculatePaymentSchedule 
} from '../utils/financialCalculations';

/**
 * PaymentArrangement-specific CRUD hook with localStorage persistence
 */
export const usePaymentArrangements = (initialData = []) => {
  // Load initial data from localStorage if available
  const loadInitialData = () => {
    if (initialData.length > 0) return initialData;
    
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PAYMENT_ARRANGEMENTS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading payment arrangements from localStorage:', error);
      return [];
    }
  };

  // Generate arrangement ID
  const generateArrangementId = () => {
    return 'arrangement_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const arrangementCrud = useCrud(loadInitialData(), generateArrangementId);

  // Override create to save to localStorage
  const createPaymentArrangement = async (arrangementData) => {
    try {
      const newArrangement = arrangementCrud.create(arrangementData);
      return { success: true, data: newArrangement };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Override update to save to localStorage
  const updatePaymentArrangement = async (id, updates) => {
    try {
      arrangementCrud.update(id, updates);
      saveToStorage(arrangementCrud.items);
      return { success: true, data: arrangementCrud.getById(id) };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Override remove to save to localStorage
  const removePaymentArrangement = (id) => {
    try {
      arrangementCrud.remove(id);
      saveToStorage(arrangementCrud.items);
      return { success: true };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Save to localStorage
  const saveToStorage = (arrangements) => {
    try {
      localStorage.setItem(STORAGE_KEYS.PAYMENT_ARRANGEMENTS, JSON.stringify(arrangements));
    } catch (error) {
      console.error('Error saving payment arrangements to localStorage:', error);
    }
  };

  // Record a payment for an arrangement
  const recordPaymentForArrangement = async (arrangementId, paymentDate = new Date()) => {
    const arrangement = arrangementCrud.getById(arrangementId);
    if (!arrangement) {
      return { success: false, errors: ['Payment arrangement not found'] };
    }

    const updatedArrangement = recordArrangementPayment(arrangement, paymentDate);
    return await updatePaymentArrangement(arrangementId, updatedArrangement);
  };

  // Get arrangements by client ID
  const getArrangementsByClientId = (clientId) => {
    return arrangementCrud.items.filter(arrangement => 
      arrangement.clientId == clientId || 
      arrangement.clientId === String(clientId) || 
      String(arrangement.clientId) === String(clientId)
    );
  };

  // Get active arrangements
  const getActiveArrangements = () => {
    return arrangementCrud.items.filter(arrangement => arrangement.isActive);
  };

  // Get overdue arrangements
  const getOverdueArrangements = (currentDate = new Date()) => {
    return arrangementCrud.items.filter(arrangement => 
      isArrangementOverdue(arrangement, currentDate)
    );
  };

  // Get arrangements due soon (within specified days)
  const getArrangementsDueSoon = (daysAhead = 7, currentDate = new Date()) => {
    return arrangementCrud.items.filter(arrangement => {
      if (!arrangement.isActive) return false;
      const daysUntilDue = getDaysUntilDue(arrangement, currentDate);
      return daysUntilDue >= 0 && daysUntilDue <= daysAhead;
    });
  };

  // Update arrears for an arrangement using financial calculation engine
  const updateArrangementArrears = async (arrangementId, missedPayments = []) => {
    const arrangement = arrangementCrud.getById(arrangementId);
    if (!arrangement) {
      return { success: false, errors: ['Payment arrangement not found'] };
    }

    const arrears = calculateMissedPaymentArrears(arrangement, missedPayments);
    return await updatePaymentArrangement(arrangementId, { arrears });
  };

  // Generate payment schedule for an arrangement
  const generateScheduleForArrangement = (arrangementId, numberOfPayments = 12) => {
    const arrangement = arrangementCrud.getById(arrangementId);
    if (!arrangement) {
      return { success: false, errors: ['Payment arrangement not found'] };
    }

    try {
      const schedule = calculatePaymentSchedule(arrangement, numberOfPayments);
      return { success: true, data: schedule };
    } catch (error) {
      return { success: false, errors: [error.message] };
    }
  };

  // Deactivate an arrangement
  const deactivateArrangement = async (arrangementId) => {
    return await updatePaymentArrangement(arrangementId, { isActive: false });
  };

  // Reactivate an arrangement
  const reactivateArrangement = async (arrangementId) => {
    return await updatePaymentArrangement(arrangementId, { isActive: true });
  };

  // Get arrangement statistics
  const getArrangementStatistics = () => {
    const total = arrangementCrud.items.length;
    const active = getActiveArrangements().length;
    const overdue = getOverdueArrangements().length;
    const dueSoon = getArrangementsDueSoon().length;
    
    const totalArrearsAmount = arrangementCrud.items.reduce(
      (total, arrangement) => total + (arrangement.arrears || 0), 
      0
    );

    return {
      total,
      active,
      overdue,
      dueSoon,
      totalArrearsAmount
    };
  };

  return {
    ...arrangementCrud,
    create: createPaymentArrangement,
    update: updatePaymentArrangement,
    remove: removePaymentArrangement,
    recordPaymentForArrangement,
    getArrangementsByClientId,
    getActiveArrangements,
    getOverdueArrangements,
    getArrangementsDueSoon,
    updateArrangementArrears,
    generateScheduleForArrangement,
    deactivateArrangement,
    reactivateArrangement,
    getArrangementStatistics,
    saveToStorage: () => saveToStorage(arrangementCrud.items) // Explicit save using current state
  };
};